<template>
    <div
        class="video-editor myWeflixEditVideo"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <main class="video-editor__container" v-if="hasReady">
            <nav class="video-editor__breadcrumb" aria-label="Trilha">
                <router-link to="/admin/colecoes">Coleções</router-link>
                <span class="video-editor__breadcrumb-sep" aria-hidden="true"><fa icon="fa-chevron-right" /></span>
                <router-link :to="'/admin/colecao/' + currentCollections.id">{{ collectionName }}</router-link>
                <span class="video-editor__breadcrumb-sep" aria-hidden="true"><fa icon="fa-chevron-right" /></span>
                <span>Editar vídeo</span>
            </nav>

            <header class="video-editor__header">
                <div>
                    <h1 class="video-editor__title">Editar Vídeo</h1>
                    <p class="video-editor__subtitle">Atualize as informações, o arquivo e a capa deste vídeo.</p>
                </div>
                <div class="video-editor__header-actions" role="toolbar" aria-label="Ações do vídeo">
                    <button type="button" class="video-editor__btn video-editor__btn--ghost" title="Mover para outra coleção" @click="showMoveDialog = true">
                        <fa icon="fa-folder-open" />
                        Mover
                    </button>
                    <button type="button" class="video-editor__btn video-editor__btn--ghost" title="Reutilizar em outra coleção" @click="showCopyDialog = true">
                        <fa icon="fa-recycle" />
                        Reutilizar
                    </button>
                    <button type="button" class="video-editor__btn video-editor__btn--delete" title="Excluir vídeo" @click="showConfirmDialog = true">
                        <fa icon="fa-trash" />
                        Excluir
                    </button>
                </div>
            </header>

            <div class="video-editor__grid">
                <div class="video-editor__column">
                    <section class="video-editor__card video-editor__card--info">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-file-lines" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Informações do vídeo</h2>
                                <p class="video-editor__card-description">Defina os dados principais da aula.</p>
                            </div>
                        </header>

                        <div class="video-editor__field-grid">
                            <div class="video-editor__field">
                                <label class="video-editor__label" for="edit-video-status">Status</label>
                                <div class="video-editor__select-wrap" :class="isActiveStatus ? 'is-on' : 'is-off'">
                                    <span class="video-editor__status-dot" aria-hidden="true"></span>
                                    <select id="edit-video-status" class="video-editor__select" v-model="status">
                                        <option :value="true">Ativado</option>
                                        <option :value="false">Desativado</option>
                                    </select>
                                </div>
                                <p v-if="Number(lessonStatus) === 5 && status" class="video-editor__hint">Processando</p>
                            </div>
                            <div class="video-editor__field">
                                <label class="video-editor__label" for="edit-video-visibility">Visibilidade</label>
                                <div class="video-editor__select-wrap" :class="{ 'is-locked': sample === false }">
                                    <fa v-if="sample === false" icon="fa-lock" class="video-editor__lock" />
                                    <select id="edit-video-visibility" class="video-editor__select" v-model="sample">
                                        <option :value="true">Gratuito</option>
                                        <option :value="false">Pago</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div class="video-editor__field video-editor__field-block">
                            <label class="video-editor__label" for="edit-video-title">Título do vídeo</label>
                            <input
                                id="edit-video-title"
                                type="text"
                                required
                                class="video-editor__input"
                                v-model="name"
                                placeholder="Ex.: Fundamentos da Gestão de Projetos"
                            />
                        </div>

                        <div class="video-editor__field video-editor__field-block">
                            <label class="video-editor__label" for="edit-video-description">Descrição do vídeo</label>
                            <textarea
                                id="edit-video-description"
                                class="video-editor__textarea"
                                v-model="shortDescription"
                                maxlength="300"
                                placeholder="Descreva o que o aluno vai aprender nesta aula..."
                            ></textarea>
                            <small class="video-editor__counter">{{ descriptionCount }}/300 caracteres</small>
                        </div>
                    </section>

                    <section class="video-editor__card video-editor__card--tips">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-lightbulb" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Dicas e boas práticas</h2>
                                <p class="video-editor__card-description">Siga estas recomendações para uma melhor experiência dos alunos.</p>
                            </div>
                        </header>
                        <div class="video-editor__tips">
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-video" /></span>
                                <strong>Formato</strong>
                                <p>MP4 recomendado ou MOV.</p>
                            </div>
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-clock" /></span>
                                <strong>Duração</strong>
                                <p>Preferencialmente entre 5 e 60 minutos.</p>
                            </div>
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-tv" /></span>
                                <strong>Resolução</strong>
                                <p>Full HD — 1920 × 1080.</p>
                            </div>
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-star" /></span>
                                <strong>Qualidade</strong>
                                <p>Áudio nítido e boa iluminação.</p>
                            </div>
                        </div>
                    </section>
                </div>

                <div class="video-editor__column">
                    <section class="video-editor__card video-editor__card--file">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-cloud-arrow-up" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Arquivo do vídeo</h2>
                                <p class="video-editor__card-description">Envie o arquivo e salve para o player ficar disponível.</p>
                            </div>
                        </header>
                        <div v-if="videoPlayer && !replaceVideo && !path">
                            <div class="video-editor__player">
                                <iframe
                                    :src="videoPlayer"
                                    title="Pré-visualização do vídeo"
                                    allow="autoplay; fullscreen"
                                    allowfullscreen
                                ></iframe>
                            </div>
                            <button type="button" class="video-editor__replace" @click="replaceVideo = true">
                                Substituir vídeo
                            </button>
                        </div>
                        <VideoUploadInput
                            v-else
                            label=""
                            :preview-url="path"
                            :file-name="selectedVideoName"
                            :file-size="selectedVideoSize"
                            accept=".mp4, .mov, .webm, .avi"
                            accept-text="Formatos aceitos conforme as regras do sistema."
                            placeholder="Clique para enviar o vídeo"
                            :disabled="isUploading"
                            :uploading="isUploading"
                            :progress="progress"
                            @file-selected="handleVideoSelected"
                            @remove-preview="handleVideoRemove"
                        />
                    </section>

                    <section class="video-editor__card video-editor__card--cover">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-image" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Capa do vídeo</h2>
                                <p class="video-editor__card-description">A capa será gerada automaticamente do vídeo.</p>
                            </div>
                        </header>
                        <ImageUploadInput
                            label=""
                            :preview-url="imagePreviewCover"
                            :accept="'.png, .jpg, .jpeg, .webp'"
                            accept-text="Tamanho: 400x400px, apenas formato PNG e JPG"
                            placeholder="Clique para fazer upload de imagem"
                            :disabled="!loaderImage"
                            @file-selected="handleImageSelected"
                            @remove-preview="handleImageRemove"
                        />
                    </section>
                </div>
            </div>
        </main>

        <footer class="video-editor__footer">
            <div class="video-editor__footer-inner">
                <button
                    type="button"
                    class="video-editor__btn video-editor__btn--back"
                    @click="$router.push('/admin/colecao/' + currentCollections.id)"
                >
                    <fa icon="fa-arrow-left" />
                    Voltar
                </button>
                <button
                    type="button"
                    class="video-editor__btn video-editor__btn--save"
                    :disabled="!loaderImage"
                    @click="editVideo()"
                >
                    <fa icon="fa-floppy-disk" />
                    Salvar alterações
                </button>
            </div>
        </footer>
    </div>

    <div class="pa-4 text-center" style="padding: 0px!important;">
        <v-dialog v-model="showConfirmDialog" max-width="320" persistent class="confirm-dialog-delete">
            <v-card>
                <v-card-title class="headline">Atenção</v-card-title>
                <v-card-text>Deseja realmente excluir este vídeo?</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="cancel" text @click="showConfirmDialog = false">Sair</v-btn>
                    <v-btn class="accept" text @click="confirmDisable">Deletar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>

    <v-dialog v-model="showMoveDialog" max-width="600" class="move-video-dialog">
        <v-card>
            <v-card-title>Mover Vídeo</v-card-title>
            <v-card-text>
                <div class="video-info">
                    <div class="video-thumbnail">
                        <img :src="imagePreviewCover" :alt="name">
                    </div>
                    <div class="video-details">
                        <h3>{{ name }}</h3>
                        <p class="duration">Duração: {{ videoTime ? converterTempo(videoTime) : '00:00' }}</p>
                    </div>
                </div>
                <div class="move-controls">
                    <p>Para qual coleção deseja mover este vídeo?</p>
                    <select v-model="selectedCollection" class="form-select">
                        <option value="">Selecione uma coleção</option>
                        <option v-for="collection in collections" :key="collection.id" :value="collection.id">
                            {{ collection.name }}
                        </option>
                    </select>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="showMoveDialog = false">Cancelar</v-btn>
                <v-btn color="primary" text :disabled="!selectedCollection" @click="moveVideo">Mover</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="showCopyDialog" max-width="600" class="copy-video-dialog">
        <v-card>
            <v-card-title>Copiar Vídeo</v-card-title>
            <v-card-text>
                <div class="move-controls">
                    <p>Selecione a coleção de origem</p>
                    <select v-model="selectedCollection" class="form-select" @change="putIdReuseVideoCollection">
                        <option value="">Selecione uma coleção</option>
                        <option v-for="collection in collections" :key="collection.id" :value="collection.id">
                            {{ collection.name }}
                        </option>
                    </select>
                </div>
            </v-card-text>
            <v-card-text>
                <div class="move-controls">
                    <p>Selecione o vídeo</p>
                    <select v-model="selectedLesson" class="form-select">
                        <option value="">Selecione um vídeo</option>
                        <option v-for="videos in videosList" :key="videos.id" :value="videos.id">
                            {{ videos.name }}
                        </option>
                    </select>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="showCopyDialog = false">Cancelar</v-btn>
                <v-btn color="primary" text @click="copyVideo">Copiar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Loader :hasReady="hasReady"/>
</template>

<script>
import Sesson from '@/utils/Weflix/sesson';
import Videos from '@/utils/Weflix/videos';
import { mapState } from 'vuex';
import Loader from '@/components/Weflix/Loader';
import apiAWS from '@/utils/storage/aws';
import companyApi from '@/utils/Weflix/company';
import LoaderImage from '@/components/Weflix/LoaderImage';
import PlayerVideo from '@/utils/storage/playerVideo';
import ImageUploadInput from '@/components/Weflix/ImageUploadInput.vue';
import VideoUploadInput from '@/components/Weflix/VideoUploadInput.vue';
import { toast } from 'vue3-toastify';
import TitlePage from '@/components/Weflix/titlePage.vue';

export default {
    data() {
        return {
            deleteItem: false,
            showConfirmDialog: false,
            season: '',
            seasonSelected: null,
            noSeason: true,
            status: false,
            lessonStatus: null,
            sample: false,
            name: '',
            shortDescription: '',
            videoFile: null,
            coverVideo: '',
            imagePreviewCover: '',
            linkVideo: '',
            path: null,
            hasReady: false,
            loaderImage: true,
            videoPlayer: '',
            hasVideotecaPath: false,
            replaceVideo: false,
            lessonMedia: {
                path: null,
                fileId: null,
                callbackUrl: null,
                uploadId: null,
                parts: null,
                filename: null,
                mediaType: null,
                size: null,
                thumbnailUploaded: null,
                token: null,
            },
            tokenVideo: '',
            videotecaUpload: {
                fileId: null,
                callbackUrl: null,
                uploadId: null,
                parts: [],
                filename: null,
                mediaType: null,
                size: 0,
                thumbnailUploaded: false,
                thumbnailUploadUrl: null,
            },
            videotecaFolderId: null,
            isUploading: false,
            uploadProgress: 0,
            isUploaded: false,
            longDescription: '',
            progress: 0,
            showMoveDialog: false,
            selectedVideo: null,
            selectedCollection: '',
            collections: [],
            showCopyDialog: false,
            lessonByCollection: [
                { id: 1, name: 'Video 1' },
                { id: 2, name: 'Video 2' },
                { id: 3, name: 'Video 3' }
            ],
            selectedLesson: null,
            filterText: '',
            filteredCollections: [],
            idReuseVideoCollection: null,
            videosList: [],
            videoTime: null
        };
    },
    computed: {
        ...mapState(['token', 'currentCollections']),
        collectionName() {
            const current = this.currentCollections;
            if (!current || Array.isArray(current)) return 'Coleção';
            return current.name || current.comercialName || 'Coleção';
        },
        descriptionCount() {
            return (this.shortDescription || '').length;
        },
        selectedVideoName() {
            return this.videoFile && this.videoFile.name ? this.videoFile.name : '';
        },
        selectedVideoSize() {
            return this.videoFile && Number(this.videoFile.size) ? Number(this.videoFile.size) : 0;
        },
        isActiveStatus() {
            return this.status === true;
        },
        filteredCollections() {
            if (!this.filterText) return this.collections;
            return this.collections.filter(item =>
                item.name.toLowerCase().includes(this.filterText.toLowerCase())
            );
        }
    },
    components: {
        ImageUploadInput,
        VideoUploadInput,
        Loader,
        LoaderImage,
        TitlePage
    },
    methods: {
        notify(message, type = "info") {
            toast(message, {
                autoClose: 3000,
                position: 'top-right',
                theme: "light",
                limit: 1,
                type: type,
                dangerouslyHTMLString: true
            });
        },
        confirmDisable() {
            this.showConfirmDialog = false;
            this.disableVideo();
        },
        closeDialog() {
            this.deleteItem = false;
        },
        handleImageSelected(file, event) {
            this.updateFileCover(event);
        },
        handleImageRemove() {
            if (this.imagePreviewCover) {
                URL.revokeObjectURL(this.imagePreviewCover);
                this.imagePreviewCover = '';
            }
        },
        handleVideoSelected(file, event) {
            this.uploadVideoAws(event);
        },
        handleVideoRemove() {
            if (this.path) {
                URL.revokeObjectURL(this.path);
                this.path = null;
            }
            this.isUploaded = false;
            this.isUploading = false;
            this.progress = 0;
            this.videotecaUpload = {
                fileId: null,
                callbackUrl: null,
                uploadId: null,
                parts: [],
                filename: null,
                mediaType: null,
                size: 0,
                thumbnailUploaded: false,
                thumbnailUploadUrl: null,
            };
        },
        updateFileCover(event) {
            const file = event.target.files[0];
            if (file) {
                this.imagePreviewCover = URL.createObjectURL(file);
            }
        },
        updateFilePathThumbnail(event) {
            this.videoFile = event.target.files[0];
        },
        updateContentLongDescription(content) {
            this.longDescription = content;
        },
        seasonsListFromApi(response) {
            if (Array.isArray(response)) return response;
            if (response?.content != null) return response.content;
            if (response?.list != null) return response.list;
            return [];
        },
        async getSesson() {
            if (!this.currentCollections?.id) return;
            try {
                const response = await Sesson.getSessonByCollection(
                    this.token,
                    this.currentCollections.id
                );
                const list = this.seasonsListFromApi(response);
                this.hasReady = true;
                if (list.length > 0) {
                    this.season = [...list].sort(
                        (a, b) => (a.order ?? 0) - (b.order ?? 0)
                    );
                    this.seasonSelected = this.season[0].id;
                    this.noSeason = false;
                } else {
                    this.noSeason = true;
                    this.newSeason();
                }
            } catch (error) {
                console.error('Erro ao obter dados da coleção:', error);
            }
        },
        putIdReuseVideoCollection() {
            this.idReuseVideoCollection = this.selectedCollection;
            this.getLessonByCollections();
        },
        async getLessonByCollections() {
            try {
                const response = await Videos.listLessonByCollections(this.token, this.idReuseVideoCollection);
                this.videosList = response;
            } catch (error) {
                console.error('Erro ao obter aulas:', error);
            }
        },
        async getSessonBy() {
            if (!this.currentCollections?.id) return;
            try {
                const response = await Sesson.getSessonByCollection(
                    this.token,
                    this.currentCollections.id
                );
                const list = this.seasonsListFromApi(response);
                this.hasReady = true;
                if (list.length > 0) {
                    this.season = [...list].sort(
                        (a, b) => (a.order ?? 0) - (b.order ?? 0)
                    );
                    this.seasonSelected = this.season[0].id;
                    this.noSeason = false;
                } else {
                    this.noSeason = true;
                    this.newSeason();
                }
            } catch (error) {
                console.error('Erro ao obter dados da coleção:', error);
            }
        },
        async newSeason() {
            const payload = {
                course: {
                    id: this.currentCollections.id
                },
                title: "Temporada 1",
                sample: 0
            };
            try {
                await Sesson.createSesson(this.token, payload);
            } catch (error) {
                console.error('Erro ao criar temporada:', error);
            }
        },
        async getVideo() {
            const param = this.$route.params.slug;
            try {
                const response = await Videos.getVideosById(this.token, param);
                this.lessonStatus = Number(response.status);
                this.status = this.lessonStatus === 1 || this.lessonStatus === 5;
                this.sample = response.sample == 1 ? true : false;
                this.name = response.title;
                this.shortDescription = response.description;
                this.videoFile = response.path;
                this.hasVideotecaPath = Boolean(response.path || response.fileId);
                this.lessonMedia = {
                    path: response.path ?? null,
                    fileId: response.fileId ?? null,
                    callbackUrl: response.callbackUrl ?? null,
                    uploadId: response.uploadId ?? null,
                    parts: response.parts ?? null,
                    filename: response.filename ?? null,
                    mediaType: response.mediaType ?? null,
                    size: response.size ?? null,
                    thumbnailUploaded: response.thumbnailUploaded ?? null,
                    token: response.token ?? null,
                };
                const thumb =
                    response.finalVideoThumbnail ||
                    response.videoThumbnail ||
                    '';
                this.coverVideo =
                    thumb && String(thumb).includes('.com/')
                        ? String(thumb).split('.com/')[1]
                        : String(thumb || '');
                this.seasonSelected = response.idModule;
                this.imagePreviewCover =
                    response.finalVideoThumbnail ||
                    response.videoThumbnail ||
                    '';
                this.hasReady = true;
                this.videoTime = response.videoTime;
                await this.loadPreviewPlayer();
            } catch (error) {
                console.error('Erro ao obter dados do video:', error);
            }
        },
        converterTempo(segundos) {
            const minutos = Math.floor(segundos / 60);
            const segundosRestantes = segundos % 60;
            const segundosFormatados = segundosRestantes.toString().padStart(2, '0');
            return `${minutos}:${segundosFormatados}s`;
        },
        async putFileAws(url, file) {
            try {
                await apiAWS.putFileAWS(url, file);
            } catch (error) {
                console.error('Erro ao enviar arquivo para AWS');
            }
        },
        async updateFileCover(event) {
            this.loaderImage = false;
            const file = event.target.files[0];
            this.imagePreviewCover = URL.createObjectURL(file);
            try {
                const thumbUrl = this.videotecaUpload?.thumbnailUploadUrl;
                if (thumbUrl) {
                    await this.putFileAws(thumbUrl, file);
                    this.videotecaUpload.thumbnailUploaded = true;
                    this.coverVideo = null;
                } else {
                    const response = await apiAWS.getSignatureImage(this.token, file.name);
                    await this.putFileAws(response.url, file);
                    this.coverVideo = response.token;
                }
                this.loaderImage = true;
            } catch (error) {
                console.error('Erro ao enviar capa', error);
                this.loaderImage = true;
            }
        },
        async ensureVideotecaFolderId() {
            if (this.videotecaFolderId) return this.videotecaFolderId;
            const status = await companyApi.getVideoteca(this.token);
            const folderId = status?.folderId || status?.idFolder || status?.idFolderVideoteca || null;
            if (!folderId) throw new Error('VIDEOTECA_FOLDER_MISSING');
            this.videotecaFolderId = String(folderId);
            return this.videotecaFolderId;
        },
        async uploadVideoAws(event) {
            try {
                const file = event.target.files[0];
                if (!file) return;
                if (Math.round(file.size / (1024 * 1024)) > 4096) {
                    this.notify('O arquivo passa de 4 GB.', 'error');
                    return;
                }
                this.path = URL.createObjectURL(file);
                this.isUploaded = false;
                this.progress = 0;
                const folderId = await this.ensureVideotecaFolderId();
                const title = (this.name || '').trim() || file.name.replace(/\.[^.]+$/, '') || file.name;
                const initResponse = await apiAWS.initVideotecaUpload(this.token, {
                    filename: file.name,
                    mediaType: file.type || 'video/mp4',
                    folderId,
                    title,
                    size: file.size,
                    metadata: {},
                });
                if (!initResponse) throw new Error('URLs de upload inválidas');
                const data = initResponse.data || initResponse;
                const multipart = data.multipart || null;
                const partUrls = Array.isArray(multipart?.partUrls)
                    ? [...multipart.partUrls].sort((a, b) => Number(a.partNumber) - Number(b.partNumber))
                    : [];
                const uploadId = multipart?.uploadId || data.uploadId || initResponse.uploadId || null;
                if (partUrls.length > 0 && !uploadId) {
                    throw new Error('Init multipart sem uploadId — não é possível finalizar o upload.');
                }
                this.videotecaUpload = {
                    fileId: data.fileId || null,
                    callbackUrl: data.callbackUrl || null,
                    uploadId,
                    partUrls,
                    partSize: Number(multipart?.partSize) || 0,
                    fileUploadUrl: data.fileUploadUrl || null,
                    thumbnailUploadUrl: data.thumbnailUploadUrl || null,
                    filename: file.name,
                    mediaType: file.type || 'video/mp4',
                    size: file.size,
                    parts: [],
                    thumbnailUploaded: false,
                    file,
                };
                await this.putVideoAwsV1(this.videotecaUpload);
            } catch (error) {
                console.error('Erro ao enviar arquivo para a Videoteca', error);
                this.isUploading = false;
                this.isUploaded = false;
                if (error?.message === 'VIDEOTECA_FOLDER_MISSING') {
                    this.notify('A empresa não tem pasta na Videoteca.', 'error');
                }
            }
        },
        async putVideoAwsV1(uploadState) {
            this.isUploading = true;
            this.isUploaded = false;
            const file = uploadState.file;
            const partUrls = uploadState.partUrls || [];
            const parts = [];
            if (partUrls.length > 0) {
                const partSize = Number(uploadState.partSize) || Math.ceil(file.size / partUrls.length);
                const progressArray = new Array(partUrls.length).fill(0);
                for (let i = 0; i < partUrls.length; i += 1) {
                    const part = partUrls[i];
                    const partNumber = Number(part.partNumber) || (i + 1);
                    const start = i * partSize;
                    const end = Math.min(start + partSize, file.size);
                    const response = await apiAWS.putVideoAWS(part.url, file.slice(start, end), (progress) => {
                        progressArray[i] = progress;
                        this.progress = progressArray.reduce((a, b) => a + b, 0) / partUrls.length;
                    });
                    if (!response?.etag) throw new Error(`ETag ausente na parte ${partNumber}`);
                    parts.push({ partNumber, etag: response.etag });
                }
            } else if (uploadState.fileUploadUrl) {
                await apiAWS.putVideoAWS(uploadState.fileUploadUrl, file, (progress) => {
                    this.progress = progress;
                });
            } else {
                throw new Error('URLs de upload inválidas');
            }
            this.videotecaUpload = {
                ...this.videotecaUpload,
                parts: uploadState.uploadId ? parts : [],
            };
            this.isUploaded = true;
            this.isUploading = false;
            this.progress = 100;
        },
        async editVideo() {
            this.hasReady = false;
            const id = this.$route.params.slug;
            const upload = this.videotecaUpload || {};
            const parts = Array.isArray(upload.parts) && upload.parts.length ? upload.parts : null;
            const media = this.lessonMedia || {};
            const lessonPayload = {
                idModule: this.seasonSelected,
                title: this.name,
                description: this.shortDescription,
                videoThumbnail: upload.thumbnailUploaded ? null : this.coverVideo,
                sample: this.sample == true ? 1 : 0,
                type: 6,
                status: this.status == true ? (Number(this.lessonStatus) === 5 ? 5 : 1) : 0,
            };
            ['path', 'fileId', 'callbackUrl', 'uploadId', 'parts', 'filename', 'mediaType', 'size', 'thumbnailUploaded', 'token'].forEach((key) => {
                if (media[key] != null && media[key] !== '') {
                    lessonPayload[key] = media[key];
                }
            });
            if (this.isUploading) {
                this.hasReady = true;
                this.notify('Aguarde o envio do arquivo terminar antes de salvar.', 'error');
                return;
            }
            try {
                if (upload.fileId) {
                    if (!this.isUploaded) {
                        throw new Error('O arquivo ainda não foi enviado para a Videoteca.');
                    }
                    const fileId = upload.fileId;
                    const uploadPayload = {
                        idModule: this.seasonSelected,
                        title: this.name,
                        description: this.shortDescription,
                        fileId,
                        callbackUrl:
                            upload.callbackUrl ||
                            `https://api.videotecaead.com.br/v1/files/${fileId}/complete`,
                    };
                    if (upload.uploadId && parts) {
                        uploadPayload.uploadId = upload.uploadId;
                        uploadPayload.parts = parts;
                    } else if (upload.uploadId || parts) {
                        throw new Error('Upload multipart incompleto: uploadId e parts precisam ir juntos.');
                    }
                    await apiAWS.editVideoVideoteca(this.token, uploadPayload, id);
                    this.hasVideotecaPath = true;
                    this.lessonMedia = {
                        ...media,
                        path: fileId,
                        fileId,
                        callbackUrl: uploadPayload.callbackUrl,
                    };
                    lessonPayload.path = fileId;
                    lessonPayload.fileId = fileId;
                    lessonPayload.callbackUrl = uploadPayload.callbackUrl;
                }
                await Videos.editLesson(this.token, lessonPayload, id);
                if (lessonPayload.fileId || lessonPayload.path) {
                    await this.loadPreviewPlayer();
                }
                if (upload.fileId) {
                    this.handleVideoRemove();
                    this.replaceVideo = false;
                }
                this.hasReady = true;
                if (!this.hasVideotecaPath && !this.videoPlayer) {
                    this.notify(
                        'Dados da aula salvos. Envie o arquivo de vídeo e salve de novo para o player funcionar.',
                        'warning'
                    );
                } else {
                    this.notify('Vídeo atualizado com sucesso!', 'success');
                }
            } catch (error) {
                this.hasReady = true;
                this.notify('Erro ao editar vídeo: ' + error.message, 'error');
                console.error('Erro ao editar vídeo', error);
            }
        },
        async disableVideo() {
            this.hasReady = false;
            const id = this.$route.params.slug;
            const media = this.lessonMedia || {};
            const payload = {
                idModule: this.seasonSelected,
                title: this.name,
                description: this.shortDescription,
                videoThumbnail: this.coverVideo,
                sample: this.sample == true ? 1 : 0,
                type: 6,
                status: 3
            };
            ['path', 'fileId', 'callbackUrl', 'token'].forEach((key) => {
                if (media[key] != null && media[key] !== '') {
                    payload[key] = media[key];
                }
            });
            try {
                await Videos.editLesson(this.token, payload, id);
                this.hasReady = true;
            } catch (error) {
                console.error('Erro ao desativar vídeo', error);
            } finally {
                this.$router.push('/admin/colecao/' + this.currentCollections.id);
            }
        },
        async loadPreviewPlayer() {
            const id = this.$route.params.slug;
            const media = this.lessonMedia || {};
            if (!id || (!media.fileId && !media.path && !this.hasVideotecaPath)) return;
            try {
                this.videoPlayer = await PlayerVideo.getPlayerVideo(this.token, id);
            } catch (error) {
                console.error('Erro ao obter o player:', error);
            }
        },
        async moveVideo() {
            const id = this.$route.params.slug;
            try {
                await Videos.moveVideo(this.token, id, this.selectedCollection);
                alert('Vídeo movido com sucesso');
                this.showMoveDialog = false;
            } catch (error) {
                console.error('Erro ao mover vídeo:', error);
            }
        },
        async getCollections() {
            try {
                const response = await Videos.listCollections(this.token);
                console.log(response);
                this.collections = response.map(item => ({
                    id: item.id,
                    name: item.name,
                    status: item.status
                }));
            } catch (error) {
                console.error('Erro ao obter coleções:', error);
            }
        }
    },
    created() {
        this.getVideo();
        this.getSesson();
        this.getCollections();
    }
};
</script>

<style>
    @import url('@/css/weflix/video-editor.css');
</style>
